import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Asset } from '../types';
import { Search, Filter, Plus, Monitor, Server, Wifi, Shield, Printer } from 'lucide-react';
const mockAssets: Asset[] = [{
  id: '1',
  name: 'NB-001-WEBER',
  type: 'Notebook',
  client: 'Zahnarztpraxis Dr. Weber',
  serialNumber: 'SN839292',
  status: 'online',
  lastSeen: 'vor 5 Min',
  ipAddress: '192.168.1.105'
}, {
  id: '2',
  name: 'SRV-DC-01',
  type: 'Server',
  client: 'Zahnarztpraxis Dr. Weber',
  serialNumber: 'SRV99283',
  status: 'online',
  lastSeen: 'vor 1 Min',
  ipAddress: '192.168.1.10'
}, {
  id: '3',
  name: 'FW-HQ-01',
  type: 'Firewall',
  client: 'Kanzlei Schmidt & Partner',
  serialNumber: 'WG-T40-992',
  status: 'warning',
  lastSeen: 'vor 2 Std',
  ipAddress: '10.0.0.1'
}, {
  id: '4',
  name: 'SW-CORE-01',
  type: 'Switch',
  client: 'Kanzlei Schmidt & Partner',
  serialNumber: 'UBI-SW-24',
  status: 'online',
  lastSeen: 'vor 1 Min',
  ipAddress: '10.0.0.2'
}, {
  id: '5',
  name: 'PC-RECEPTION',
  type: 'PC',
  client: 'Zahnarztpraxis Dr. Hoffmann',
  serialNumber: 'DELL-3929',
  status: 'offline',
  lastSeen: 'vor 2 Tagen',
  ipAddress: '192.168.178.20'
}, {
  id: '6',
  name: 'AP-LOBBY',
  type: 'Access Point',
  client: 'Zahnarztpraxis Dr. Hoffmann',
  serialNumber: 'UBI-AP-PRO',
  status: 'online',
  lastSeen: 'vor 1 Min',
  ipAddress: '192.168.178.5'
}];
interface AssetManagementProps {
  onLogout: () => void;
}
export function AssetManagement({
  onLogout
}: AssetManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const getIcon = (type: Asset['type']) => {
    switch (type) {
      case 'Notebook':
      case 'PC':
        return <Monitor className="h-4 w-4 text-slate-500" data-id="element-276" />;
      case 'Server':
        return <Server className="h-4 w-4 text-purple-500" data-id="element-277" />;
      case 'Firewall':
        return <Shield className="h-4 w-4 text-red-500" data-id="element-278" />;
      case 'Switch':
      case 'Access Point':
        return <Wifi className="h-4 w-4 text-blue-500" data-id="element-279" />;
      case 'Printer':
        return <Printer className="h-4 w-4 text-amber-500" data-id="element-280" />;
      default:
        return <Monitor className="h-4 w-4 text-slate-500" data-id="element-281" />;
    }
  };
  const getStatusBadge = (status: Asset['status']) => {
    switch (status) {
      case 'online':
        return <Badge variant="success" data-id="element-282">Online</Badge>;
      case 'offline':
        return <Badge variant="error" data-id="element-283">Offline</Badge>;
      case 'warning':
        return <Badge variant="warning" data-id="element-284">Warnung</Badge>;
      case 'maintenance':
        return <Badge variant="info" data-id="element-285">Wartung</Badge>;
      default:
        return <Badge variant="secondary" data-id="element-286">{status}</Badge>;
    }
  };
  const filteredAssets = mockAssets.filter(asset => (clientFilter === 'all' || asset.client === clientFilter) && (asset.name.toLowerCase().includes(searchTerm.toLowerCase()) || asset.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) || asset.ipAddress?.includes(searchTerm)));
  return <MainLayout title="IT Asset Management" onLogout={onLogout} data-id="element-287">
      <Card data-id="element-288">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-start md:items-center" data-id="element-289">
          <div className="flex gap-4 flex-1 w-full md:w-auto" data-id="element-290">
            <div className="w-full md:w-64" data-id="element-291">
              <Input placeholder="Suchen (Name, IP, Serial)..." icon={<Search className="h-4 w-4" data-id="element-293" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-292" />
            </div>
            <div className="w-full md:w-48" data-id="element-294">
              <Select options={[{
              value: 'all',
              label: 'Alle Kunden'
            }, {
              value: 'Zahnarztpraxis Dr. Weber',
              label: 'Zahnarztpraxis Dr. Weber'
            }, {
              value: 'Kanzlei Schmidt & Partner',
              label: 'Kanzlei Schmidt & Partner'
            }, {
              value: 'Zahnarztpraxis Dr. Hoffmann',
              label: 'Zahnarztpraxis Dr. Hoffmann'
            }]} value={clientFilter} onChange={e => setClientFilter(e.target.value)} data-id="element-295" />
            </div>
            <Button variant="outline" icon={<Filter className="h-4 w-4" data-id="element-297" />} data-id="element-296">
              Filter
            </Button>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" data-id="element-299" />} data-id="element-298">Asset anlegen</Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-300">
          <table className="w-full text-sm text-left" data-id="element-301">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-302">
              <tr data-id="element-303">
                <th className="px-4 py-3 font-medium" data-id="element-304">Name / Typ</th>
                <th className="px-4 py-3 font-medium" data-id="element-305">Kunde</th>
                <th className="px-4 py-3 font-medium" data-id="element-306">IP / Serial</th>
                <th className="px-4 py-3 font-medium" data-id="element-307">Status</th>
                <th className="px-4 py-3 font-medium" data-id="element-308">Letzter Kontakt</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-309">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-310">
              {filteredAssets.map(asset => <tr key={asset.id} className="hover:bg-slate-50 transition-colors" data-id="element-311">
                  <td className="px-4 py-3" data-id="element-312">
                    <div className="flex items-center gap-3" data-id="element-313">
                      <div className="p-2 bg-slate-100 rounded-lg" data-id="element-314">
                        {getIcon(asset.type)}
                      </div>
                      <div data-id="element-315">
                        <div className="font-medium text-slate-900" data-id="element-316">
                          {asset.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-317">
                          {asset.type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-318">{asset.client}</td>
                  <td className="px-4 py-3" data-id="element-319">
                    <div className="text-slate-900" data-id="element-320">
                      {asset.ipAddress || '-'}
                    </div>
                    <div className="text-xs text-slate-500 font-mono" data-id="element-321">
                      {asset.serialNumber}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-322">{getStatusBadge(asset.status)}</td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-323">{asset.lastSeen}</td>
                  <td className="px-4 py-3 text-right" data-id="element-324">
                    <Button variant="ghost" size="sm" data-id="element-325">
                      Details
                    </Button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {filteredAssets.length === 0 && <div className="text-center py-12 text-slate-500" data-id="element-326">
            Keine Assets gefunden.
          </div>}
      </Card>
    </MainLayout>;
}